// src/components/DashboardLogin.jsx
import React, { useState } from 'react';
import { Mail, Lock, ArrowRight } from 'lucide-react';
import { signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { auth, db } from '../services/firebase';
import { doc, getDoc } from 'firebase/firestore';

export default function DashboardLogin({ onLoginSuccess, tipo }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showResetForm, setShowResetForm] = useState(false);
  const [resetMessage, setResetMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      const userDoc = await getDoc(doc(db, 'usuarios', user.uid));
      let role = 'gestor';
      let plan = 'entry';

      if (userDoc.exists()) {
        role = String(userDoc.data().role || userDoc.data().userType || 'gestor').toLowerCase();
        plan = userDoc.data().plan || 'entry';
      }

      localStorage.setItem('phoenix_user_type', role);
      localStorage.setItem('phoenix_active_plan', plan);
      localStorage.setItem('phoenix_user_email', email);

      onLoginSuccess({ user, role, plan });
    } catch (err) {
      console.error('[PHOENIX] Erro de login no Dashboard:', err.code);
      setError('Email ou senha inválidos. Verifique os dados usados na assinatura.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setResetMessage('');
    if (!email) {
      setResetMessage('Digite seu email acima antes de solicitar a recuperação.');
      return;
    }
    try {
      await sendPasswordResetEmail(auth, email, { url: 'https://app.phoenixos.com.br' });
      setResetMessage('Enviamos um link de redefinição para o seu email.');
    } catch (err) {
      console.error('[PHOENIX] Erro ao enviar redefinição de senha:', err.code);
      setResetMessage('Não foi possível enviar o email. Verifique se o endereço está correto.');
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#010208] flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <h1 className="text-3xl font-black italic tracking-tighter text-white">
            PHOENIX<span className="text-lime-400">OS</span>
          </h1>
          {/* 🔥 NOVO: selo visual dizendo qual Dashboard é esse — sem isso, a
              única pista era a URL na barra do navegador, fácil de não notar. */}
          {tipo && (
            <p className={`inline-block mt-3 mb-1 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${tipo === 'consultor'
                ? 'bg-purple-600/20 border border-purple-400/30 text-purple-300'
                : 'bg-blue-600/20 border border-blue-400/30 text-blue-300'
              }`}>
              Painel {tipo === 'consultor' ? 'Consultor' : 'Gestor'}
            </p>
          )}
          <p className="text-[11px] text-slate-500 uppercase tracking-widest font-bold mt-2">
            Acesse sua conta para continuar
          </p>
        </div>

        <div className="bg-slate-900/40 border border-white/5 rounded-[2rem] p-8">
          {!showResetForm ? (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="text-[10px] text-slate-500 uppercase tracking-widest font-bold">Email</label>
                <div className="flex items-center gap-3 bg-black/30 border border-white/10 rounded-xl px-4 py-3 mt-1">
                  <Mail size={16} className="text-slate-500" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="seu@email.com"
                    className="bg-transparent w-full text-white text-sm outline-none placeholder:text-slate-600"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="text-[10px] text-slate-500 uppercase tracking-widest font-bold">Senha</label>
                <div className="flex items-center gap-3 bg-black/30 border border-white/10 rounded-xl px-4 py-3 mt-1">
                  <Lock size={16} className="text-slate-500" />
                  <input
                    type="password" autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="bg-transparent w-full text-white text-sm outline-none placeholder:text-slate-600"
                    required
                  />
                </div>
              </div>

              {error && (
                <p className="text-red-400 text-xs font-bold text-center">{error}</p>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-lime-400 hover:bg-lime-300 text-black font-black uppercase tracking-widest text-xs py-4 rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {isLoading ? 'Entrando...' : 'Entrar'} <ArrowRight size={14} />
              </button>

              <button
                type="button"
                onClick={() => { setShowResetForm(true); setResetMessage(''); }}
                className="w-full text-center text-[11px] text-slate-500 hover:text-lime-400 font-bold uppercase tracking-widest transition-colors"
              >
                Esqueci minha senha
              </button>
            </form>
          ) : (
            <div className="space-y-5">
              <p className="text-xs text-slate-400 leading-relaxed">
                Digite o email da sua conta acima e enviaremos um link para redefinir sua senha.
              </p>
              {resetMessage && (
                <p className="text-[11px] text-lime-400 font-bold text-center">{resetMessage}</p>
              )}
              <button
                onClick={handleResetPassword}
                className="w-full bg-white/10 hover:bg-white/20 text-white font-black uppercase tracking-widest text-xs py-4 rounded-xl transition-all"
              >
                Enviar Link de Redefinição
              </button>
              <button
                onClick={() => setShowResetForm(false)}
                className="w-full text-center text-[11px] text-slate-500 hover:text-white font-bold uppercase tracking-widest transition-colors"
              >
                ← Voltar ao login
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
